import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    tag: '作品更新',
    title: '风起长街',
    subtitle: '长街之上风起云涌，一位女子以智慧搅动整座王朝的棋局',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20elegant%20asian%20woman%20in%20flowing%20red%20traditional%20robes%20standing%20in%20a%20lantern%20lit%20ancient%20chinese%20street%20at%20dusk%2C%20wind%20lifting%20her%20sleeves%2C%20deep%20crimson%20and%20ink%20black%20tones%20with%20warm%20golden%20highlights%2C%20dramatic%20moody%20cinematic%20lighting%2C%20epic%20atmospheric%20composition&width=1920&height=900&seq=natsume-hero-01&orientation=landscape',
    to: '/detail/fengqi-changjie',
  },
  {
    tag: '作品更新',
    title: '无声告白',
    subtitle: '一场没有凶器、没有证人、也没有声音的案件，真相藏在沉默之中',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20asian%20woman%20in%20a%20dim%20room%20lit%20by%20a%20single%20cold%20window%20light%2C%20her%20face%20half%20in%20shadow%2C%20deep%20teal%20and%20charcoal%20tones%20with%20a%20hint%20of%20amber%2C%20tense%20atmospheric%20lighting%2C%20moody%20cinematic%20composition&width=1920&height=900&seq=natsume-hero-02&orientation=landscape',
    to: '/detail/wusheng-gaobai',
  },
  {
    tag: '新闻报道',
    title: '东京迷途',
    subtitle: '雨夜、霓虹与一桩悬案，刑警桐生凛在迷雾重重的都市里追查真相',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20asian%20woman%20in%20a%20trench%20coat%20walking%20through%20a%20rainy%20neon%20tokyo%20alley%20at%20night%2C%20wet%20reflective%20asphalt%2C%20deep%20blue%20and%20cold%20cyan%20tones%20with%20a%20single%20warm%20light%2C%20moody%20atmospheric%20cinematic%20lighting%2C%20wide%20composition&width=1920&height=900&seq=natsume-hero-03&orientation=landscape',
    to: '/detail/dongjing-mitu',
  },
  {
    tag: '作品更新',
    title: '最后的画师',
    subtitle: '在传统绘画的余晖中，一位女性守护着即将失传的技艺',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20asian%20woman%20in%20a%20traditional%20art%20studio%20holding%20a%20painting%20brush%2C%20warm%20lantern%20light%2C%20rich%20earth%20and%20deep%20indigo%20tones%2C%20paint%20splattered%20wooden%20desk%2C%20moody%20cinematic%20lighting%2C%20atmospheric%20composition&width=1920&height=900&seq=natsume-hero-04&orientation=landscape',
    to: '/detail/zuihou-de-huashi',
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    timer.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [index]);

  const resetTimer = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(index + (delta < 0 ? 1 : -1));
      resetTimer();
    }
  };

  return (
    <section
      className="relative w-full h-[520px] md:h-[600px] lg:h-[640px] overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.title}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} - 夏目彩春影视作品`}
            className={`w-full h-full object-top object-cover ${i === index ? 'animate-slow-zoom' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/10" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full max-w-content mx-auto px-4 md:px-8 flex flex-col justify-center">
        <div key={index} className="max-w-2xl animate-fade-up">
          <span className="inline-block px-3 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-label tracking-widest mb-5">
            {slides[index].tag}
          </span>
          <h1 className="font-heading font-black text-4xl md:text-6xl text-background-50 leading-tight">
            {slides[index].title}
          </h1>
          <p className="mt-5 text-base md:text-lg text-background-100/90 leading-relaxed max-w-xl">
            {slides[index].subtitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={slides[index].to}
              className="px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看作品详情
            </Link>
            <Link
              to="/category"
              className="px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部作品
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-10 w-full max-w-content mx-auto px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.title}
              type="button"
              aria-label={`切换到 ${slide.title}`}
              onClick={() => {
                go(i);
                resetTimer();
              }}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                i === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/50 hover:bg-background-50/80'
              }`}
            />
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => {
              go(index - 1);
              resetTimer();
            }}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => {
              go(index + 1);
              resetTimer();
            }}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/40 text-background-50 hover:bg-background-50/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}